import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Upload, message } from 'antd';
import { CloudUploadOutlined, FileTextOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Box, Flex, Text } from 'atoms';
import InfoBanner from 'componentsv2/common/InfoBanner';
import {
	WAREHOUSE_ENTITY_BAG,
	WAREHOUSE_ENTITY_MANPOWER,
	WAREHOUSE_ENTITY_SHIPMENT,
	WAREHOUSE_UPLOAD_BAG,
	WAREHOUSE_UPLOAD_MANPOWER,
	WAREHOUSE_UPLOAD_SHIPMENT,
	uploadWarehouseCsvV2,
} from 'store/warehousingSliceV2';
import {
	CSV_HEADERS,
	LINE_ITEM_BAG,
	LINE_ITEM_MANPOWER,
	LINE_ITEM_SHIPMENT,
	MANPOWER_CSV_HEADERS_COST_PLUS,
	MODEL_COST_PLUS,
} from '../warehousing.constants';

const UploadArea = styled(Box)`
	border: 2px dashed #d9d9d9;
	border-radius: 8px;
	padding: 32px 20px;
	text-align: center;
	background: #fafafa;
	margin-bottom: 16px;
`;

const FileRow = styled(Flex)`
	align-items: center;
	gap: 10px;
	justify-content: center;
	font-size: 13px;
	color: #18405d;
`;

const TemplateLink = styled.button`
	border: none;
	background: transparent;
	color: #18405d;
	text-decoration: underline;
	cursor: pointer;
	font-size: 13px;
	padding: 0;
`;

const ResultList = styled.ul`
	margin: 8px 0 0;
	padding-left: 18px;
	max-height: 180px;
	overflow-y: auto;
	font-size: 12px;
	color: #b11500;

	li {
		margin-bottom: 4px;
	}
`;

// The service compares the header row exactly and rejects the whole file on mismatch,
// so the download is the header row and nothing else. It deliberately carries no example
// values: a sample cost or count is invented data, and a sample date goes stale and has
// whoever fills it in submitting back-dated rows the Daily tab then flags as late. The
// columns are spelled out in each panel's own description.
const TEMPLATES = {
	[LINE_ITEM_MANPOWER]: {
		uploadType: WAREHOUSE_UPLOAD_MANPOWER,
		entity: WAREHOUSE_ENTITY_MANPOWER,
		fileName: 'warehouse-manpower-template.csv',
		header: CSV_HEADERS[LINE_ITEM_MANPOWER],
		title: 'Upload manpower CSV',
	},
	[LINE_ITEM_SHIPMENT]: {
		uploadType: WAREHOUSE_UPLOAD_SHIPMENT,
		entity: WAREHOUSE_ENTITY_SHIPMENT,
		fileName: 'warehouse-shipment-template.csv',
		header: CSV_HEADERS[LINE_ITEM_SHIPMENT],
		title: 'Upload shipments CSV',
	},
	[LINE_ITEM_BAG]: {
		uploadType: WAREHOUSE_UPLOAD_BAG,
		entity: WAREHOUSE_ENTITY_BAG,
		fileName: 'warehouse-bag-template.csv',
		header: CSV_HEADERS[LINE_ITEM_BAG],
		title: 'Upload bag count CSV',
	},
};

const downloadTemplate = (template) => {
	const lines = [template.header.join(',')];
	const blob = new Blob([`${lines.join('\n')}\n`], {
		type: 'text/csv;charset=utf-8;',
	});
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = template.fileName;
	link.click();
	URL.revokeObjectURL(url);
};

/**
 * CSV upload for daily manpower / shipment data.
 *
 * The file is PUT to S3 via a signed URL first; the API then receives only the key.
 * Two outcomes must read differently: a *partial* success (some rows rejected, the rest
 * saved) and an outright rejection (bad file or header — nothing is persisted).
 */
export default function WarehouseCsvUploadModal({
	open,
	entryType,
	projectId,
	modelType,
	onCancel,
	onDone,
}) {
	const dispatch = useDispatch();
	const base = TEMPLATES[entryType] || TEMPLATES[LINE_ITEM_MANPOWER];
	// Cost Plus manpower carries an extra CostToCustomer column — only manpower feeds
	// cost, so shipments/bags keep their template regardless of model_type.
	const template =
		entryType === LINE_ITEM_MANPOWER && modelType === MODEL_COST_PLUS
			? {
					...base,
					fileName: 'warehouse-manpower-cost-plus-template.csv',
					header: MANPOWER_CSV_HEADERS_COST_PLUS,
			  }
			: base;

	const [file, setFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [result, setResult] = useState(null);
	const [rejected, setRejected] = useState(false);

	const handleClose = () => {
		setFile(null);
		setResult(null);
		setRejected(false);
		onCancel();
	};

	const handleBeforeUpload = (selected) => {
		const isCsv = selected.type === 'text/csv' || selected.name.toLowerCase().endsWith('.csv');

		if (!isCsv) {
			message.error('Only .csv files can be uploaded.');
			return Upload.LIST_IGNORE;
		}

		setFile(selected);
		setResult(null);
		setRejected(false);
		return false; // upload happens on confirm, not on select
	};

	const handleUpload = async () => {
		if (!file) {
			message.error('Select a CSV file first.');
			return;
		}

		setUploading(true);
		setRejected(false);
		try {
			const data = await dispatch(
				uploadWarehouseCsvV2({
					upload_type: template.uploadType,
					entity: template.entity,
					project_id: projectId,
					file,
				})
			);
			setResult(data);
			onDone();
		} catch (error) {
			// A 422 means the file or header was rejected and nothing was persisted
			setRejected(true);
		} finally {
			setUploading(false);
		}
	};

	const errors = result?.errors || [];
	const successCount = result?.success_count ?? 0;
	const errorCount = result?.error_count ?? 0;

	return (
		<Modal
			open={open}
			title={template.title}
			okText={result ? 'Done' : 'Upload'}
			confirmLoading={uploading}
			onOk={result ? handleClose : handleUpload}
			onCancel={handleClose}
			destroyOnClose
		>
			{!result && (
				<>
					<UploadArea>
						<Upload
							accept=".csv"
							maxCount={1}
							beforeUpload={handleBeforeUpload}
							showUploadList={false}
						>
							<Box>
								<CloudUploadOutlined style={{ fontSize: 28, color: '#18405d' }} />
								<Text
									style={{
										display: 'block',
										margin: '10px 0',
										fontSize: 13,
										color: '#8c8c8c',
									}}
								>
									Select a .csv file to upload
								</Text>
							</Box>
						</Upload>

						{file && (
							<FileRow>
								<FileTextOutlined />
								{file.name}
							</FileRow>
						)}
					</UploadArea>

					<TemplateLink type="button" onClick={() => downloadTemplate(template)}>
						Download template
					</TemplateLink>

					<InfoBanner tone="neutral">
						The header row must match exactly: {template.header.join(', ')}
						<br />
						EntryDate accepts either YYYY-MM-DD or DD-MM-YYYY, with either{' '}
						<code>-</code> or <code>/</code> as the separator (e.g. 2026-08-01,
						01-08-2026, or 01/08/2026) — Excel commonly rewrites dates to one of these
						when a sheet is edited and re-saved.
					</InfoBanner>

					{rejected && (
						<InfoBanner tone="warning">
							The file was rejected before any row was read — no entries were saved.
							Check that it is a .csv and that the header row matches exactly.
						</InfoBanner>
					)}
				</>
			)}

			{result && (
				<>
					<InfoBanner tone={errorCount > 0 ? 'warning' : 'info'}>
						{errorCount > 0
							? `${successCount} of ${
									result.row_count ?? successCount + errorCount
							  } rows saved — ${errorCount} rejected. The saved rows are already applied.`
							: `All ${successCount} rows saved successfully.`}
					</InfoBanner>

					{errors.length > 0 && (
						<ResultList>
							{errors.map((rowError, index) => (
								// eslint-disable-next-line react/no-array-index-key
								<li key={`row-${rowError.row ?? index}`}>
									{rowError.row ? `Row ${rowError.row}: ` : ''}
									{/* The service reports the failure reason under `error`, not
									    `message` — reading the wrong key here previously rendered
									    the literal text "[object Object]" instead of the reason. */}
									{rowError.error || rowError.message || 'Unknown error'}
								</li>
							))}
						</ResultList>
					)}
				</>
			)}
		</Modal>
	);
}

WarehouseCsvUploadModal.propTypes = {
	open: PropTypes.bool,
	entryType: PropTypes.string.isRequired,
	projectId: PropTypes.number.isRequired,
	modelType: PropTypes.string,
	onCancel: PropTypes.func.isRequired,
	onDone: PropTypes.func.isRequired,
};

WarehouseCsvUploadModal.defaultProps = {
	open: false,
	modelType: undefined,
};
